import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
  IconFilter,
  IconX,
} from '@leland-dev/leland-ui-library';
import React, { type FC } from 'react';

import { type Scalars } from '../../__generated-gql-types__/globalTypes';
import useMediaQuery, { Breakpoints } from '../../utils/hooks/useMediaQuery';
import { type OfferingPackagesFilterInputs } from '../../utils/package';
import { type SchoolFilterHookOutput } from '../filters/SchoolFilters';

import { useOfferingPackagesFiltersModal } from './OfferingPackagesFiltersModal';
import { type OfferingPackagesCoachingFocusFragment } from './__generated-gql-types__/OfferingPackages.generated';

interface OfferingPackagesFilterButtonProps {
  categoryUrn: Scalars['CategoryUrn']['output'];
  coachingFocuses?: Nullable<OfferingPackagesCoachingFocusFragment[]>;
  comprehensiveFocuses?: Nullable<Array<Scalars['CoachingFocusUrn']['output']>>;
  defaultValues: Nullable<OfferingPackagesFilterInputs>;
  defaultTotalOfferingPackages: number;
  onRefetchPackages: (variables: OfferingPackagesFilterInputs) => Promise<void>;
  clearFilters: () => void;
  className?: string;
  schoolFilters: SchoolFilterHookOutput;
}

const OfferingPackagesFilterButton: FC<OfferingPackagesFilterButtonProps> = ({
  categoryUrn,
  coachingFocuses,
  comprehensiveFocuses,
  defaultValues,
  defaultTotalOfferingPackages,
  onRefetchPackages,
  clearFilters,
  schoolFilters,
  className = '',
}) => {
  const isMd = useMediaQuery(Breakpoints.MD);

  const {
    setOpen: setOfferingPackagesFiltersModalOpen,
    Modal: OfferingPackagesFiltersModal,
  } = useOfferingPackagesFiltersModal();

  const numberOfFiltersApplied =
    (defaultValues?.coachingFocusListFilter
      ? defaultValues?.coachingFocusListFilter.length
      : 0) +
    (defaultValues?.priceFilter?.start ? 1 : 0) +
    (defaultValues?.priceFilter?.end ? 1 : 0) +
    (schoolFilters.selectedSchools.length ?? 0);

  return (
    <>
      <div
        className={`${
          numberOfFiltersApplied > 0 ? 'relative mr-1 lg:mr-0' : ''
        } w-full md:w-fit ${className}`}
      >
        <Button
          buttonColor={ButtonColor.WHITE}
          selected={numberOfFiltersApplied > 0}
          width={ButtonWidth.FULL}
          size={isMd ? ButtonSize.LARGE : ButtonSize.MEDIUM}
          onClick={() => setOfferingPackagesFiltersModalOpen(true)}
          label={isMd ? 'Filters' : 'Filter Packages'}
          LeftIcon={IconFilter}
          id="offering_packages_filter_button--filter_button"
        />
        {numberOfFiltersApplied > 0 ? (
          <button
            type="button"
            className="group absolute -right-1.5 -top-1.5 flex size-6 items-center justify-center rounded-full border-2 border-white bg-leland-gray-dark hover:border-leland-gray-dark hover:bg-white"
            onClick={clearFilters}
            id="offering_packages_filter_button--clear_filters"
          >
            <p className="text-base font-semibold text-white group-hover:hidden">
              {numberOfFiltersApplied}
            </p>
            <IconX className="hidden size-3 text-leland-gray-dark group-hover:block" />
          </button>
        ) : null}
      </div>

      <OfferingPackagesFiltersModal
        categoryUrn={categoryUrn}
        coachingFocuses={coachingFocuses}
        comprehensiveFocuses={comprehensiveFocuses}
        defaultValues={defaultValues}
        defaultTotalOfferingPackages={defaultTotalOfferingPackages}
        isDesktop={isMd}
        onRefetchPackages={onRefetchPackages}
        schoolFilters={schoolFilters}
      />
    </>
  );
};

export default OfferingPackagesFilterButton;
